import React, { useEffect, useState } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./Reports.css";

const BASE_URL = "/api";

function ProtectedImage({ filename, token, alt = "image", className = "report-img" }) {
  const [imgSrc, setImgSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const getFileName = (path) => {
    if (!path) return null;
    try {
      if (path.startsWith("http://") || path.startsWith("https://")) {
        const url = new URL(path);
        const parts = url.pathname.split("/");
        return parts.pop() || parts.pop();
      }
    } catch (e) { }
    const parts = path.split("/");
    return parts.pop();
  };

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;

    const loadImage = async () => {
      setError(false);
      setLoading(true);
      setImgSrc(null);

      const name = getFileName(filename);
      if (!name) {
        setLoading(false);
        return;
      }

      try {
        const url = `${BASE_URL}/files/view?name=${encodeURIComponent(name)}`;
        const res = await axios.get(url, {
          headers: { Authorization: token ? `Bearer ${token}` : undefined },
          responseType: "blob",
        });

        if (cancelled) return;

        objectUrl = URL.createObjectURL(res.data);
        setImgSrc(objectUrl);
      } catch (e) {
        console.error("ProtectedImage load error:", e);
        setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadImage();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [filename, token]);

  if (!filename) return <span>�</span>;
  if (loading) return <span style={{ color: "#666" }}>Loading�</span>;
  if (error) return <span style={{ color: "#b00" }}>Failed</span>;
  return imgSrc ? <img src={imgSrc} alt={alt} className={className} /> : <span>�</span>;
}

const formatDate = (d) => {
  if (!d) return "�";
  const parsed = new Date(d);
  return isNaN(parsed.getTime()) ? "�" : parsed.toLocaleString();
};



const getImageBase64Sync = async (filename, token) => {
  if (!filename) return null;

  try {
    const name = filename.split("/").pop();
    const res = await axios.get(
      `${BASE_URL}/files/view?name=${encodeURIComponent(name)}`,
      {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
        responseType: "blob",
      }
    );

    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(res.data);
    });
  } catch {
    return null;
  }
};



const Reports = () => {
  const [societies, setSocieties] = useState([]);
  const [societyName, setSocietyName] = useState("");
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedReport, setSelectedReport] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchSocieties = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/admin/names`, {
          headers: { Authorization: token ? `Bearer ${token}` : undefined },
        });
        setSocieties(res.data);
      } catch (err) {
        console.error("Failed to fetch societies", err);
      }
    };
    fetchSocieties();
  }, [token]);

  const fetchReports = async (society, start, end) => {
    try {
      setLoading(true);

      let url = `${BASE_URL}/admin/visitor-requests/latest`;

      if (society && start && end) {
        url =
          `${BASE_URL}/admin/visitor-requests/by-date` +
          `?societyName=${encodeURIComponent(society)}` +
          `&start=${start}T00:00:00` +
          `&end=${end}T23:59:59`;
      }

      const res = await axios.get(url, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });

      const data = Array.isArray(res.data) ? res.data : [res.data];
      setReports(data);
      setFilteredReports(data);
      setError("");
    } catch (err) {
      console.error(err);
      setError("Failed to fetch visitor reports.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleApplyFilter = () => {
    if (societyName && startDate && endDate) {
      fetchReports(societyName, startDate, endDate);
    }
  };

  useEffect(() => {
    const q = searchQuery.toLowerCase();
    const filtered = reports.filter(
      (r) =>
        r.guestName?.toLowerCase().includes(q) ||
        r.flatNumber?.toString().toLowerCase().includes(q) ||
        r.societyName?.toLowerCase().includes(q)
    );
    setFilteredReports(filtered);
  }, [searchQuery, reports]);

  // =========================
  // ? ADDED: PDF DOWNLOAD
  // =========================
  const handleDownloadPDF = async () => {
    if (!filteredReports.length) {
      alert("No data to download");
      return;
    }

    setPdfLoading(true); // ? START LOADER

    try {
      const doc = new jsPDF("landscape");

      // preload images
      const imageCache = {};
      for (const r of filteredReports) {
        imageCache[r.id] = {
          photo1: await getImageBase64Sync(r.photo1, token),
          photo2: await getImageBase64Sync(r.photo2, token),
        };
      }

      autoTable(doc, {
        startY: 20,
        head: [[
          "Guest",
          "Flat",
          "Society",
          "Status",
          "Check-in",
          "Check-out",
          "Photo 1",
          "Photo 2"
        ]],

        body: filteredReports.map((r) => [
          r.guestName,
          r.flatNumber,
          r.societyName,
          r.status,
          formatDate(r.checkInTime),
          formatDate(r.checkOutTime),
          "",
          "",
        ]),

        styles: { fontSize: 8, minCellHeight: 40 },

        didDrawCell: (data) => {
          if (data.section !== "body") return;

          const rowIndex = data.row.index;
          const colIndex = data.column.index;

          const report = filteredReports[rowIndex];
          if (!report) return;

          const images = imageCache[report.id];
          if (!images) return;

          if (colIndex === 6 && images.photo1) {
            doc.addImage(
              images.photo1,
              "JPEG",
              data.cell.x + 2,
              data.cell.y + 2,
              30,
              30
            );
          }

          if (colIndex === 7 && images.photo2) {
            doc.addImage(
              images.photo2,
              "JPEG",
              data.cell.x + 2,
              data.cell.y + 2,
              30,
              30
            );
          }
        },
      });

      doc.save(`visitor-reports-with-row-images.pdf`);
    } finally {
      setPdfLoading(false); // ? STOP LOADER (always runs)
    }
  };




  return (
    <div className="reports-page">
      <h2> Visitor Reports</h2>

      {pdfLoading && (
        <div className="pdf-loading">
          Generating PDF, please wait...
        </div>
      )}

      <div className="report-filter-bar">
        <h3> Filter Reports</h3>

        <div className="filter-controls">

          {/* Society */}
          <div className="filter-item">
            <label>Society</label>
            <select
              value={societyName}
              onChange={(e) => setSocietyName(e.target.value)}
            >
              <option value="">Select Society</option>
              {societies.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {/* Start Date */}
          <div className="filter-item">
            <label>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          {/* End Date */}
          <div className="filter-item">
            <label>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          {/* Buttons */}
          <button className="filter-btn" onClick={handleApplyFilter}>
            Apply
          </button>

          <button
            className="filter-btn"
            onClick={handleDownloadPDF}
            disabled={pdfLoading}
          >
            {pdfLoading ? "Generating PDF..." : "Download PDF"}
          </button>

        </div>
      </div>

      <input
        type="text"
        placeholder="Search..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />

      {loading ? (
        <p>Loading�</p>
      ) : error ? (
        <p className="error">{error}</p>
      ) : (
        <table className="reports-table">
          <thead>
            <tr>
              <th>Guest</th>
              <th>Flat</th>
              <th>Society</th>
              <th>Status</th>
              <th>Check-in</th>
              <th>Check-out</th>
              <th>Photo 1</th>
              <th>Photo 2</th>
            </tr>
          </thead>
          <tbody>
            {filteredReports.length ? filteredReports.map(r => (
              <tr
                key={r.id}
                onClick={() => setSelectedReport(r)}
                className={!r.checkOutTime ? "missing-checkout" : ""}
              >
                <td>{r.guestName}</td>
                <td>{r.flatNumber}</td>
                <td>{r.societyName}</td>
                <td>{r.status}</td>
                <td>{formatDate(r.checkInTime)}</td>
                <td>{formatDate(r.checkOutTime)}</td>
                <td>{r.photo1 && <ProtectedImage filename={r.photo1} token={token} />}</td>
                <td>{r.photo2 && <ProtectedImage filename={r.photo2} token={token} />}</td>
              </tr>
            )) : (
              <tr>
                <td colSpan="8">No records found</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );

};

export default Reports;
